import { useLayoutEffect, useState, type RefObject } from "react";

interface IUseInitialHorizontalScrollArgs {
    scrollerRef: RefObject<HTMLDivElement | null>;
    initialScrollPosition?: "start" | "end";
    columnsCount: number;
    isScrollable: boolean;
}

interface IUseScrollFadesArgs {
    scrollerRef: RefObject<HTMLDivElement | null>;
    gridRef: RefObject<HTMLDivElement | null>;
    columnsCount: number;
    rowsCount: number;
    hasHeaderRow: boolean;
}

interface IUseResponsiveGridLayoutArgs {
    viewportRef: RefObject<HTMLDivElement | null>;
    columnsCount: number;
    fallbackCellMinWidth: number;
    fallbackGap: number;
}

interface IResponsiveGridLayout {
    gridTemplateColumns: string;
    gridWidth: string;
    isScrollable: boolean;
}

const getScrollFadeState = (scroller: HTMLDivElement) => {
    const maxScrollLeft = Math.max(scroller.scrollWidth - scroller.clientWidth, 0);
    const hasOverflow = maxScrollLeft > 1;

    return {
        showStartFade: hasOverflow && scroller.scrollLeft > 1,
        showEndFade: hasOverflow && scroller.scrollLeft < maxScrollLeft - 1,
    };
};

const getResolvedCssNumber = (element: HTMLElement, variableName: string, fallbackValue: number) => {
    const rawValue = getComputedStyle(element).getPropertyValue(variableName).trim();
    const parsedValue = Number.parseFloat(rawValue);

    return Number.isFinite(parsedValue) ? parsedValue : fallbackValue;
};

const useInitialHorizontalScroll = ({
    scrollerRef,
    initialScrollPosition,
    columnsCount,
    isScrollable,
}: IUseInitialHorizontalScrollArgs) => {
    useLayoutEffect(() => {
        const scroller = scrollerRef.current;

        if (!scroller) {
            return;
        }

        if (!initialScrollPosition || !isScrollable) {
            scroller.scrollTo({ left: 0 });
            return;
        }

        scroller.scrollTo({
            left: initialScrollPosition === "end" ? scroller.scrollWidth - scroller.clientWidth : 0,
        });
    }, [columnsCount, initialScrollPosition, isScrollable, scrollerRef]);
};

const useScrollFades = ({ scrollerRef, gridRef, columnsCount, rowsCount, hasHeaderRow }: IUseScrollFadesArgs) => {
    const [scrollFadeState, setScrollFadeState] = useState({
        showStartFade: false,
        showEndFade: false,
    });

    useLayoutEffect(() => {
        const scroller = scrollerRef.current;

        if (!scroller) {
            return;
        }

        const syncScrollFadeState = () => {
            const nextState = getScrollFadeState(scroller);

            setScrollFadeState((currentState) =>
                currentState.showStartFade === nextState.showStartFade &&
                currentState.showEndFade === nextState.showEndFade
                    ? currentState
                    : nextState,
            );
        };

        syncScrollFadeState();
        scroller.addEventListener("scroll", syncScrollFadeState, { passive: true });

        if (typeof ResizeObserver === "undefined") {
            return () => {
                scroller.removeEventListener("scroll", syncScrollFadeState);
            };
        }

        const resizeObserver = new ResizeObserver(syncScrollFadeState);
        resizeObserver.observe(scroller);

        if (gridRef.current) {
            resizeObserver.observe(gridRef.current);
        }

        return () => {
            scroller.removeEventListener("scroll", syncScrollFadeState);
            resizeObserver.disconnect();
        };
    }, [columnsCount, gridRef, hasHeaderRow, rowsCount, scrollerRef]);

    return scrollFadeState;
};

const useResponsiveGridLayout = ({
    viewportRef,
    columnsCount,
    fallbackCellMinWidth,
    fallbackGap,
}: IUseResponsiveGridLayoutArgs): IResponsiveGridLayout => {
    const [layoutMetrics, setLayoutMetrics] = useState({
        availableWidth: 0,
        resolvedCellMinWidth: fallbackCellMinWidth,
        resolvedGap: fallbackGap,
    });

    useLayoutEffect(() => {
        const viewport = viewportRef.current;

        if (!viewport) {
            return;
        }

        const measureLayout = () => {
            const nextMetrics = {
                availableWidth: viewport.clientWidth,
                resolvedCellMinWidth: getResolvedCssNumber(viewport, "--heatmap-cell-min-width", fallbackCellMinWidth),
                resolvedGap: getResolvedCssNumber(viewport, "--heatmap-gap", fallbackGap),
            };

            setLayoutMetrics((currentMetrics) =>
                currentMetrics.availableWidth === nextMetrics.availableWidth &&
                currentMetrics.resolvedCellMinWidth === nextMetrics.resolvedCellMinWidth &&
                currentMetrics.resolvedGap === nextMetrics.resolvedGap
                    ? currentMetrics
                    : nextMetrics,
            );
        };

        measureLayout();

        if (typeof ResizeObserver === "undefined") {
            return;
        }

        const resizeObserver = new ResizeObserver(measureLayout);
        resizeObserver.observe(viewport);

        return () => {
            resizeObserver.disconnect();
        };
    }, [fallbackCellMinWidth, fallbackGap, viewportRef]);

    const minGridWidth =
        columnsCount > 0
            ? columnsCount * layoutMetrics.resolvedCellMinWidth +
              Math.max(columnsCount - 1, 0) * layoutMetrics.resolvedGap
            : 0;
    const fitsWithoutScroll = layoutMetrics.availableWidth <= 0 || minGridWidth <= layoutMetrics.availableWidth;

    return {
        gridTemplateColumns:
            columnsCount === 0
                ? "none"
                : fitsWithoutScroll
                  ? `repeat(${columnsCount}, minmax(0, 1fr))`
                  : `repeat(${columnsCount}, ${layoutMetrics.resolvedCellMinWidth}px)`,
        gridWidth: fitsWithoutScroll ? "100%" : `${minGridWidth}px`,
        isScrollable: !fitsWithoutScroll,
    };
};

export { useInitialHorizontalScroll, useResponsiveGridLayout, useScrollFades };
